/**
 * Messages API layer
 * Handles fetching and managing message data from the backend
 */

import { queryOptions } from '@tanstack/react-query';
import { apiClient } from './base/client';
import { ApiError } from './base/errors';
import { ThoughtProcess, Reference } from '../features/chat/types/conversation';

/**
 * Thought Process API parameters
 */
export interface ThoughtProcessParams {
    conversationID: string;
    messageID: string;
}

/**
 * Custom error class for thoughtprocess-specific errors
 */
export class ThoughtProcessApiError extends ApiError {
    public readonly name: string = 'ThoughtProcessApiError';

    constructor(message: string, status?: number, code?: string) {
        super(message, status, code);
        Object.setPrototypeOf(this, ThoughtProcessApiError.prototype);
    }
}

/**
 * Fetch thought process from the backend
 */
export async function fetchThoughtProcess(params: ThoughtProcessParams): Promise<ThoughtProcess> {
    try {
        if (!params.conversationID) {
            throw new ThoughtProcessApiError(
                'Conversation ID is required',
                400,
                'MISSING_CONVERSATION_ID'
            );
        } else if (!params.messageID) {
            throw new ThoughtProcessApiError(
                'Message ID is required',
                400,
                'MISSING_MESSAGE_ID'
            );
        }

        // Build query parameters
        const queryParams = new URLSearchParams();
        queryParams.append('conversationID', params.conversationID);
        queryParams.append('message_id', params.messageID);
        const endpoint = `/thoughtprocess?${queryParams.toString()}`;

        // Make the API request - backend returns array with single item
        const response = await apiClient.get<ThoughtProcess[]>(endpoint);

        // Validate response
        if (!Array.isArray(response)) {
            throw new ThoughtProcessApiError(
                'Invalid response format: expected array',
                500,
                'INVALID_RESPONSE'
            );
        }

        if (response.length === 0) {
            throw new ThoughtProcessApiError(
                'Thought Process not found',
                404,
                'THOUGHT_PROCESS_NOT_FOUND'
            );
        }

        const thoughtProcessData = response[0];

        // Validate thought process data structure
        if (!thoughtProcessData || !thoughtProcessData.conversationID || !thoughtProcessData.message_id || !thoughtProcessData.thoughtprocess || !Array.isArray(thoughtProcessData.datapoints) || !Array.isArray(thoughtProcessData.agent_classification)) {
            throw new ThoughtProcessApiError(
                'Invalid thought process data structure',
                500,
                'INVALID_THOUGHT_PROCESS_DATA'
            );
        }

        return thoughtProcessData;
    } catch (error) {
        if (error instanceof ThoughtProcessApiError) {
            throw error;
        } else {
            throw new ThoughtProcessApiError(
                `Failed to fetch conversation detail: ${error instanceof Error ? error.message : 'Unknown error'}`,
                500,
                'FETCH_DETAIL_FAILED'
            );
        }
    }
}

/**
 * Query key factory for thought process
 * Provides consistent cache keys for TanStack Query
 */
export const thoughtProcessQueryKeys = {
    all: ['thoughtProcess'] as const,
    details: () => [...thoughtProcessQueryKeys.all, 'details'] as const,
    detail: (id: string) => [...thoughtProcessQueryKeys.details(), id] as const,
};

/**
 * TanStack Query options for fetching thought process
 * Provides reusable, type-safe query configuration
 */
export const thoughtProcessQueryOptions = (params: ThoughtProcessParams) =>
    queryOptions({
        queryKey: thoughtProcessQueryKeys.detail(`${params.conversationID}-${params.messageID}`),
        queryFn: () => fetchThoughtProcess(params),
        staleTime: Infinity, // Thought Process for a particular message should not change
        gcTime: 10 * 60 * 1000, // 10 minutes
        retry:  (failureCount: number, error: any) => {
            // Don't retry authentication errors
            if (error instanceof ApiError && error.status === 401) {
                return false;
            }

            // Special case: DO retry 404 errors (Not Found)
            if (error instanceof ApiError && error.status === 404) {
                // Only retry up to 3 times
                return failureCount < 3;
            }

            // Don't retry client errors (4xx)
            if (error instanceof ApiError && error.status && error.status >= 400 && error.status < 500) {
                return false;
            }

            // Retry network errors and server errors up to 3 times
            return failureCount < 3;
        },
        retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
    });
